import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import api from "../services/api";
import "./LaborHoursList.css";

/** 印刷時は約11人/ページ。末尾が1〜3人だけのページになる場合は前行にまとめて紙を節約（最大14行/ページになり得る） */
function chunkRowsForPrint(rows, pageSize = 11, mergeTailIfAtMost = 3) {
  if (!rows || rows.length === 0) return [];
  if (rows.length <= pageSize) return [rows.slice()];

  const chunks = [];
  for (let i = 0; i < rows.length; i += pageSize) {
    chunks.push(rows.slice(i, i + pageSize));
  }
  if (chunks.length >= 2) {
    const last = chunks[chunks.length - 1];
    if (last.length > 0 && last.length <= mergeTailIfAtMost) {
      chunks[chunks.length - 2] = chunks[chunks.length - 2].concat(last);
      chunks.pop();
    }
  }
  return chunks;
}

/**
 * fromYear/fromMonth 〜 toYear/toMonth の (year, month, key, label) リストを生成
 */
function buildMonthRange(fromYear, fromMonth, toYear, toMonth) {
  const arr = [];
  let y = fromYear;
  let m = fromMonth;
  while (y < toYear || (y === toYear && m <= toMonth)) {
    arr.push({ year: y, month: m, key: `${y}-${m}`, label: `${y}年${m}月` });
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return arr;
}

/**
 * 労働時間一覧：社員コード・名前・部署名・選択年月範囲の労働時間・総合・前月分合計/平均
 */
const LaborHoursList = () => {
  const location = useLocation();
  const stateDept = location.state?.departmentName ?? "";
  const stateEndDate = location.state?.printEndDate ?? "";
  const [departmentName, setDepartmentName] = useState(stateDept);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // 初期値: endDate を基準に14か月前〜現在
  const defaultEndDate = stateEndDate || new Date().toISOString().slice(0, 10);
  const defaultEndObj = new Date(`${defaultEndDate.slice(0, 10)}T12:00:00`);
  const defaultToYear = defaultEndObj.getFullYear();
  const defaultToMonth = defaultEndObj.getMonth() + 1;
  const defaultFrom = (() => {
    let y = defaultToYear;
    let m = defaultToMonth - 13;
    while (m <= 0) { m += 12; y--; }
    return { year: y, month: m };
  })();

  const [fromYear, setFromYear] = useState(defaultFrom.year);
  const [fromMonth, setFromMonth] = useState(defaultFrom.month);
  const [toYear, setToYear] = useState(defaultToYear);
  const [toMonth, setToMonth] = useState(defaultToMonth);

  // 年選択肢: 現在の5年前〜1年後
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 7 }, (_, i) => currentYear - 5 + i);

  // 選択中の月リスト（古い→新しい順。データ計算用）
  const months = useMemo(
    () => buildMonthRange(fromYear, fromMonth, toYear, toMonth),
    [fromYear, fromMonth, toYear, toMonth]
  );

  // 表示用：最新月が左、古い月が右になるよう逆順。先頭(最新月)が「当月=進行中」
  const displayMonths = useMemo(
    () => months.slice().reverse().map((m, idx) => ({ ...m, isCurrent: idx === 0 })),
    [months]
  );

  // 当月セル装飾用スタイル
  const currentMonthHeaderStyle = {
    backgroundColor: "#fef3c7",
    color: "#92400e",
    fontWeight: 700,
  };
  const currentMonthCellStyle = {
    backgroundColor: "#fffbeb",
  };

  // API に渡す日付
  const startDateParam = `${fromYear}-${String(fromMonth).padStart(2, "0")}-01`;
  // 終了日は選択月の「末日」を渡す（API は date__lte=end_date のため、月初を渡すと
  // 当月の途中までのデータしか拾えない不具合を回避）
  const endDateLastDay = new Date(toYear, toMonth, 0).getDate();
  const endDateParam = `${toYear}-${String(toMonth).padStart(2, "0")}-${String(endDateLastDay).padStart(2, "0")}`;

  const printChunks = useMemo(() => chunkRowsForPrint(rows, 11, 3), [rows]);

  // 前月分: 最終月を除いた全月
  const prevMonthInfo = months.length >= 2 ? months[months.length - 2] : null;
  const prevMonthsCount = Math.max(months.length - 1, 0);
  const prevColLabel = prevMonthInfo
    ? `前月までの${prevMonthsCount}ヶ月分`
    : "前月まで";

  const prevMonthLabel = useMemo(() => {
    if (months.length === 0) return "";
    if (months.length === 1) {
      const f = months[0];
      return `${f.year}年${f.month}月のみのため前月分集計なし`;
    }
    const first = months[0];
    const last = months[months.length - 2];
    return `${first.year}年${first.month}月〜${last.year}年${last.month}月の合計・平均（÷${months.length - 1}）`;
  }, [months]);

  useEffect(() => {
    // from > to の場合はデータ取得しない
    if (fromYear > toYear || (fromYear === toYear && fromMonth > toMonth)) {
      setRows([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get("/api/worktime/admin/labor-hours-list/", {
        params: {
          ...(departmentName ? { departmentName } : {}),
          startDate: startDateParam,
          endDate: endDateParam,
        },
      })
      .then((res) => {
        if (!cancelled) setRows(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [departmentName, startDateParam, endDateParam]);

  const rangeLabel = months.length > 0
    ? `${months[0].year}年${months[0].month}月〜${months[months.length - 1].year}年${months[months.length - 1].month}月`
    : "（範囲未選択）";

  return (
    <div className="worktimes-container labor-hours-list">
      <div className="labor-hours-list-header">
        <div className="labor-hours-nav-screen no-print">
          <Link to="/admin/workstime" className="labor-hours-list-back">← 出退勤管理</Link>
          <Link to="/admin/worktime-daily" className="labor-hours-list-back">
            日別勤怠・メモ
          </Link>
          <Link
            to="/admin/labor-hours-analytics"
            state={{ departmentName, printEndDate: endDateParam }}
            className="labor-hours-list-back"
            style={{ fontWeight: 600 }}
          >
            勤務分析・グラフ
          </Link>
          <Link
            to={`/admin/labor-hours/analytics`}
            state={{
              departmentName,
              printEndDate: endDateParam,
            }}
            className="btn-ai-analyze"

          >
            AI分析
          </Link>
        </div>

        {/* 年月範囲セレクター */}
        <div className="labor-hours-range-selector no-print">
          <span className="labor-hours-range-label">表示期間：</span>
          <select
            value={fromYear}
            onChange={(e) => setFromYear(Number(e.target.value))}
            className="labor-hours-year-select"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}年</option>
            ))}
          </select>
          <select
            value={fromMonth}
            onChange={(e) => setFromMonth(Number(e.target.value))}
            className="labor-hours-month-select"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{m}月</option>
            ))}
          </select>
          <span className="labor-hours-range-separator">〜</span>
          <select
            value={toYear}
            onChange={(e) => setToYear(Number(e.target.value))}
            className="labor-hours-year-select"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}年</option>
            ))}
          </select>
          <select
            value={toMonth}
            onChange={(e) => setToMonth(Number(e.target.value))}
            className="labor-hours-month-select"
          >
            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
              <option key={m} value={m}>{m}月</option>
            ))}
          </select>
        </div>

        <h2 style={{ textAlign: "center", color: "#555" }}>労働時間一覧</h2>
        <div className="detail-header-row">
          <h2 className="employee-detail-name" style={{ textAlign: "center" }}>
            労働時間一覧（{departmentName ? `部署: ${departmentName} / ` : ""}
            {rangeLabel}）
          </h2>
        </div>
      </div>

      {(fromYear > toYear || (fromYear === toYear && fromMonth > toMonth)) ? (
        <p style={{ textAlign: "center", color: "#c0392b", padding: 24 }}>
          開始年月が終了年月より後になっています。期間を正しく設定してください。
        </p>
      ) : loading ? (
        <p className="labor-hours-list-loading">読み込み中...</p>
      ) : (
        <div className="labor-hours-list-table-wrap">
          <table className="labor-hours-list-table">
            <thead>
              <tr>
                <th>社員コード</th>
                <th>名前</th>
                <th>部署</th>
                <th>課</th>
                <th>総労働時間（{prevColLabel}）</th>
                <th>平均時間（{prevColLabel}）</th>
                {displayMonths.map(({ key, label, isCurrent }) => (
                  <th
                    key={key}
                    style={isCurrent ? currentMonthHeaderStyle : undefined}
                    title={isCurrent ? "進行中の月（平均集計には含めません）" : undefined}
                  >
                    {isCurrent ? `★ ${label}（進行中）` : label}
                  </th>
                ))}
              </tr>
            </thead>
            {printChunks.map((chunk, ci) => {
              const rowOffset = printChunks.slice(0, ci).reduce((n, c) => n + c.length, 0);
              return (
              <tbody key={`chunk-${ci}`} className="labor-hours-print-chunk">
                {chunk.map((r, ri) => {
                  const stripe = (rowOffset + ri) % 2 === 1;
                  return (
                    <tr
                      key={r.memberId}
                      className={stripe ? "labor-hours-row-alt" : "labor-hours-row"}
                    >
                      <td>{r.memberId}</td>
                      <td>{r.name}</td>
                      <td>
                        {r.departmentBuName != null && String(r.departmentBuName).trim() !== ""
                          ? r.departmentBuName
                          : "—"}
                      </td>
                      <td>
                        {r.kaDisplay != null && r.kaDisplay !== ""
                          ? r.kaDisplay
                          : r.kaName || "—"}
                      </td>
                      <td className="labor-hours-list-total">
                        {r.totalHoursPrevMonth != null ? Number(r.totalHoursPrevMonth).toFixed(2) : "—"}
                      </td>
                      <td>
                        {r.averageHoursPrevMonth != null ? Number(r.averageHoursPrevMonth).toFixed(2) : "—"}
                      </td>
                      {displayMonths.map(({ key, isCurrent }) => {
                        const val = r.monthHours && r.monthHours[key];
                        return (
                          <td
                            key={key}
                            style={isCurrent ? currentMonthCellStyle : undefined}
                          >
                            {val != null ? Number(val).toFixed(2) : "—"}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            );
            })}
          </table>
        </div>
      )}
    </div>
  );
};

export default LaborHoursList;
